import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ApiResponse } from '@shared/models';
import { catchError, filter, map, mergeMap } from 'rxjs/operators';
import { ProductCategory, Product } from '@shared/models';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { LocalizeRouterService } from '@app/i18n';
import { WindowService } from '@shared/window';

@Injectable({
    providedIn: 'root',
})
export class HttpService {
    private httpClient = inject(HttpClient);
    private translateService = inject(TranslateService);
    private router = inject(Router);
    private localizeRouterService = inject(LocalizeRouterService);
    private windowService = inject(WindowService);

    getCategory(slug: string): Observable<ProductCategory> {
        return this.httpClient.get<ApiResponse<ProductCategory>>(`api/public/category/${slug}`).pipe(
            map((response) => {
                return response.data;
            }),
        );
    }

    getProduct(slug: string): Observable<Product> {
        return this.httpClient
            .skipErrorHandler()
            .get<ApiResponse<Product>>(`api/public/product/${slug}`)
            .pipe(
                map((response) => {
                    return response.data;
                }),
                catchError(() => {
                    return this.getProductsAdditionals(slug).pipe(
                        catchError((error) => {
                            void this.router.navigate(this.localizeRouterService.translateRoute(['/not-found']), {
                                skipLocationChange: true,
                            });
                            throw error;
                        }),
                    );
                }),
            );
    }

    getProductsAdditionals(slug: string): Observable<Product> {
        return this.httpClient
            .get(`api/public/products-classic/csv/${this.translateService.currentLang}`, {
                observe: 'response',
                responseType: 'blob',
            })
            .pipe(
                map((response) => response.body),
                filter((body) => body !== null),
                mergeMap((body) => this.csvToArray(body)),
                map((products) => {
                    const product = products.find((product) => product.slug === slug);
                    if (!product) {
                        throw new Error('');
                    }
                    return product;
                }),
            );
    }

    csvToArray(csvFile: Blob): Observable<Product[]> {
        if (this.windowService.isBrowser) {
            const fileReader = new FileReader();
            fileReader.readAsText(csvFile);

            return new Observable((observer): void => {
                fileReader.onload = (e) => {
                    const csvString = <string>e.target?.result;
                    const response = csvString.split('\n').map(function (line) {
                        return line.split(';');
                    });
                    response.shift();
                    const products = response
                        .filter((response) => response.length === 6)
                        .map(([category, stelmachId, width, height, weight, imageUrl]) => ({
                            id: stelmachId,
                            slug: stelmachId,
                            name: stelmachId,
                            description: '',
                            patternName: stelmachId,
                            image: {
                                url: imageUrl,
                            },
                            categories: [
                                {
                                    id: category,
                                    name: category,
                                    slug: category,
                                    description: '',
                                    featured: false,
                                    image: {
                                        url: imageUrl,
                                    },
                                },
                            ],
                            parameters: {
                                height: height?.slice(0, -2) || '',
                                height_z_kam: height?.slice(0, -2) || '',
                                width: width?.slice(0, -2) || '',
                                width_z_kam: width?.slice(0, -2) || '',
                                stone_label: '',
                                default_ct_count: '',
                                weight: weight,
                            },
                        }));

                    observer.next(products);
                    observer.complete();
                };

                fileReader.onerror = (error) => {
                    observer.error(error);
                };
            });
        }
        return of([]);
    }
}
